import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM, { createPortal } from "react-dom";
import {
  FiX,
  FiSearch,
  FiUser,
  FiShield,
  FiCheck,
  FiLoader,
  FiAlertCircle,
  FiChevronDown,
} from "react-icons/fi";
import { QRCodeSVG } from "qrcode.react";
import api from "../../../connector/api";

// --- CUSTOM PORTAL DROPDOWN ---
function CustomDropdown({ label, value, options, onChange, icon: Icon }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const selectedOption =
    options.find((opt) => String(opt.value) === String(value)) || options[0];

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 160),
      });
    }
  };

  const toggleDropdown = () => {
    if (!isOpen) updateCoords();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (isOpen) updateCoords();
    };
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !e.target.closest(".portal-dropdown-menu")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium cursor-pointer"
      >
        <div className="flex items-center gap-2 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
          <span className="truncate text-zinc-700">{selectedOption?.label}</span>
        </div>
        <FiChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
            }}
            className="portal-dropdown-menu fixed bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-[10000] animate-in fade-in zoom-in-95 duration-100 max-h-56 overflow-y-auto"
          >
            {label && (
              <div className="px-3 py-1 border-b border-zinc-100 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                {label}
              </div>
            )}
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors text-xs cursor-pointer ${
                  String(value) === String(option.value)
                    ? "bg-zinc-100 text-zinc-900 font-semibold"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <span className="truncate">{option.label}</span>
                {String(value) === String(option.value) && (
                  <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

export default function CreateStudentPassModal({ isOpen, onClose, onSaveSuccess }) {
  // Form State
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [passType, setPassType] = useState("PARENT");

  // Student Search / Autocomplete State
  const [studentSearch, setStudentSearch] = useState("");
  const [studentOptions, setStudentOptions] = useState([]);
  const [searchingStudents, setSearchingStudents] = useState(false);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);

  // Submission State
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const searchContainerRef = useRef(null);

  // Pass Type Options matching Django PassType Choices
  const passTypeOptions = useMemo(
    () => [
      { value: "PARENT", label: "Parent Pickup" },
      { value: "SELF", label: "Self / Drive / Walk" },
      { value: "RIDE_HAILING", label: "Grab / Pass App" },
    ],
    []
  );

  // Generated Preview ID Code (Simulated based on backend save logic)
  const previewCardCode = useMemo(() => {
    if (!selectedStudent) return "2026-PU-000";
    if (passType === "SELF") {
      return (
        selectedStudent.student_id_number ||
        selectedStudent.student_uid ||
        `STU-${selectedStudent.id}`
      );
    }
    const currentYear = new Date().getFullYear();
    return `${currentYear}-PU-${selectedStudent.id}`;
  }, [selectedStudent, passType]);

  // --- FETCH STUDENTS FOR AUTOCOMPLETE ---
  useEffect(() => {
    if (!studentSearch.trim()) {
      setStudentOptions([]);
      setIsSearchDropdownOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingStudents(true);
      try {
        const response = await api.get(
          `/students/?search=${encodeURIComponent(studentSearch)}&page_size=5`
        );
        const data = response.data?.results || response.data || [];
        setStudentOptions(Array.isArray(data) ? data : []);
        setIsSearchDropdownOpen(true);
      } catch (err) {
        console.error("Error searching students:", err);
      } finally {
        setSearchingStudents(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [studentSearch]);

  // Click outside listener for student search dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setIsSearchDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset modal state on open/close
  useEffect(() => {
    if (!isOpen) {
      setSelectedStudent(null);
      setPassType("PARENT");
      setStudentSearch("");
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  // EARLY RETURN PLACED AFTER ALL HOOK DECLARATIONS
  if (!isOpen) return null;

  // --- SUBMIT HANDLER ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStudent) {
      setError("Please select a student first.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        student: selectedStudent.id,
        pass_type: passType,
      };

      const response = await api.post("/student-pass-card/", payload);

      if (onSaveSuccess) {
        onSaveSuccess(response.data);
      }
      onClose();
    } catch (err) {
      console.error("Error creating student pass card:", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          "Failed to generate student pass card."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/50 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white border border-zinc-200/90 rounded-lg shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200/80 bg-zinc-50/50">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-zinc-900 text-white rounded-lg">
              <FiShield className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                Issue Student Pass Card
              </h3>
              <p className="text-[11px] text-zinc-500">
                Generate or update a dismissal pass card for a student.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body: Two Column Form & Live Preview */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-600">
              <FiAlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {/* LEFT SIDE: Inputs (7 Cols) */}
            <div className="md:col-span-7 space-y-4">
              {/* Student Autocomplete Search */}
              <div ref={searchContainerRef} className="relative">
                <label className="block text-[11px] font-bold text-zinc-500 uppercase mb-1">
                  Select Student <span className="text-red-500">*</span>
                </label>

                {selectedStudent ? (
                  <div className="flex items-center justify-between p-2.5 bg-zinc-50 border border-zinc-200/80 rounded-lg">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center overflow-hidden">
                        {selectedStudent.photo ? (
                          <img
                            src={selectedStudent.photo}
                            alt="Student"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FiUser className="w-4 h-4 text-zinc-500" />
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-zinc-900">
                          {selectedStudent.last_name}, {selectedStudent.first_name}
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono">
                          {selectedStudent.student_id_number ||
                            selectedStudent.student_uid ||
                            "No ID"}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedStudent(null)}
                      className="text-xs text-zinc-400 hover:text-red-600 transition-colors p-1"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <FiSearch className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input
                      type="text"
                      placeholder="Type student name or ID..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      className="w-full pl-9 pr-8 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400 transition-colors"
                    />
                    {searchingStudents && (
                      <FiLoader className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 animate-spin" />
                    )}

                    {/* Autocomplete Dropdown List */}
                    {isSearchDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto divide-y divide-zinc-100">
                        {studentOptions.length > 0 ? (
                          studentOptions.map((stu) => (
                            <button
                              key={stu.id}
                              type="button"
                              onClick={() => {
                                setSelectedStudent(stu);
                                setIsSearchDropdownOpen(false);
                                setStudentSearch("");
                              }}
                              className="w-full text-left p-2.5 hover:bg-zinc-50 transition-colors flex items-center justify-between text-xs"
                            >
                              <div>
                                <span className="font-semibold text-zinc-900">
                                  {stu.last_name}, {stu.first_name}
                                </span>
                                <span className="text-[10px] text-zinc-400 block font-mono">
                                  {stu.student_id_number ||
                                    stu.student_uid ||
                                    "No ID"}
                                </span>
                              </div>
                              <span className="text-[10px] font-medium text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded">
                                {stu.grade_section || stu.grade_level || "Student"}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="p-3 text-center text-xs text-zinc-400">
                            No students found.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Pass Type Custom Dropdown */}
              <div>
                <label className="block text-[11px] font-bold text-zinc-500 uppercase mb-1">
                  Pass Type Option <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  label="Pass Type"
                  value={passType}
                  options={passTypeOptions}
                  onChange={(val) => setPassType(val)}
                  icon={FiShield}
                />
              </div>

              <div className="p-3 bg-zinc-50 border border-zinc-200/60 rounded-lg text-[11px] text-zinc-500 space-y-1">
                <p className="font-bold text-zinc-700 uppercase">System Rules:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>
                    <strong>Parent Pickup:</strong> Auto-generates a year-prefixed code (e.g. 2026-PU-102).
                  </li>
                  <li>
                    <strong>Self Dismissal:</strong> Linked directly to student's official ID.
                  </li>
                </ul>
              </div>
            </div>

            {/* RIGHT SIDE: Dynamic Card Preview (5 Cols) */}
            <div className="md:col-span-5 flex flex-col items-center justify-center border-l border-zinc-100 pl-0 md:pl-5">
              <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-2">
                Card Generated Preview
              </span>

              {/* Printable CR80 Standard Pass Card (#002855 & White Theme) */}
              <div
                style={{ backgroundColor: "#002855" }}
                className="w-56 h-[320px] text-white rounded-xl shadow-md p-3.5 flex flex-col justify-between border border-blue-900/40 relative overflow-hidden shrink-0"
              >
                {/* Background Accent */}
                <div className="absolute -top-10 -right-10 w-24 h-24 bg-white/5 rounded-full blur-lg pointer-events-none" />

                {/* Card Header */}
                <div className="text-center space-y-0.5 border-b border-white/20 pb-2 z-10">
                  <h4 className="text-[9px] font-extrabold uppercase tracking-widest text-white">
                    ACADEMIC INSTITUTION
                  </h4>
                  <p className="text-[8px] font-semibold text-blue-200 tracking-wider uppercase">
                    OFFICIAL STUDENT PASS
                  </p>
                </div>

                {/* Avatar / Photo */}
                <div className="flex flex-col items-center my-auto space-y-1.5 z-10">
                  <div className="w-16 h-16 rounded-full border-2 border-white/30 bg-white/10 flex items-center justify-center overflow-hidden shadow-xs backdrop-blur-xs">
                    {selectedStudent?.photo ? (
                      <img
                        src={selectedStudent.photo}
                        alt="Student"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FiUser className="w-7 h-7 text-blue-200" />
                    )}
                  </div>
                  <div className="text-center">
                    <h5 className="text-xs font-bold text-white tracking-wide truncate max-w-[180px]">
                      {selectedStudent
                        ? `${selectedStudent.last_name}, ${selectedStudent.first_name}`
                        : "Select Student"}
                    </h5>
                    <p className="text-[9px] text-blue-200 font-medium">
                      {passTypeOptions.find((p) => p.value === passType)?.label}
                    </p>
                  </div>
                </div>

                {/* Card Footer Details */}
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-2 space-y-1 z-10 border border-white/15">
                  <div className="flex justify-between items-center text-[9px] text-blue-100">
                    <span className="font-medium text-blue-200">Pass Code:</span>
                    <span className="font-mono font-bold text-white">
                      {previewCardCode}
                    </span>
                  </div>

                  <div className="pt-1.5 border-t border-white/15 flex justify-center">
                    <div className="bg-white p-1 rounded-sm shadow-xs">
                      <QRCodeSVG
                        value={previewCardCode}
                        size={38}
                        fgColor="#002855"
                        bgColor="#FFFFFF"
                        level="M"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer Controls */}
          <div className="flex items-center justify-end gap-2 pt-5 mt-5 border-t border-zinc-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedStudent}
              className="px-4 py-2 bg-zinc-900 text-white rounded-lg text-xs font-medium hover:bg-zinc-800 disabled:opacity-50 transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              {submitting && <FiLoader className="w-3.5 h-3.5 animate-spin" />}
              <span>Generate Pass Card</span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}